import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Search, Loader2, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useCommunity } from '../hooks/useCommunity';
import { api } from '../api';
import ConfirmModal from '../components/ConfirmModal';
import DiscussionCard from '../components/DiscussionCard';
import EmptyState from '../components/EmptyState';

const CommunityDiscussions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  
  const { community, loading: communityLoading, isAdmin } = useCommunity(id);
  const [communityDiscussions, setCommunityDiscussions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });

  const handleVote = async (discId) => {
    try {
      const res = await api.post(`/discussions/${discId}/vote`);
      setCommunityDiscussions(prev => prev.map(d => d.id === discId ? { ...d, likes: res.likes } : d));
    } catch (err) {
      console.error('Failed to vote', err);
    }
  };

  useEffect(() => {
    const fetchDiscussions = async () => {
      try {
        setLoading(true);
        const discussionsData = await api.get(`/discussions?community_id=${id}`);
        setCommunityDiscussions(Array.isArray(discussionsData?.data) ? discussionsData.data : (Array.isArray(discussionsData) ? discussionsData : []));
      } catch (err) {
        console.error('Failed to fetch community discussions', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDiscussions();
  }, [id]);

  const filteredDiscussions = communityDiscussions
    .filter(d => 
      d.title?.toLowerCase().includes(search.toLowerCase()) || 
      d.content?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const handleDelete = (discId) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Discussion?',
      description: 'Are you sure you want to delete this discussion? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await api.delete(`/discussions/${discId}`);
          setCommunityDiscussions(prev => prev.filter(d => d.id !== discId));
          addToast('Discussion deleted successfully', 'success');
          setConfirmConfig({ isOpen: false });
        } catch (err) {
          console.error('Failed to delete discussion', err);
          addToast('Failed to delete discussion', 'error');
        }
      }
    });
  };

  return (
    <div className="min-w-0 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12">
            <div className="text-center md:text-left">
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">Community Discussions</h1>
              <p className="text-gray-500 font-medium max-w-xl">
                {community ? `Knowledge and conversations from the ${community.name} hub.` : 'Persistent knowledge and group conversations.'}
              </p>
            </div>
            <button 
              onClick={() => navigate(`/c/${id}/d/new`)}
              className="w-full md:w-auto flex items-center justify-center gap-2 bg-brand-indigo text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-brand-indigo/10 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] text-xs uppercase tracking-widest"
            >
              <Plus size={20} />
              New Topic
            </button>
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 p-2 mb-10 flex items-center shadow-sm focus-within:ring-4 focus-within:ring-brand-indigo/5 transition-all">
             <div className="p-3 text-gray-400">
                <Search size={20} />
             </div>
             <input 
               type="text" 
               value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder="Search discussions by title or content..."
               className="flex-1 bg-transparent border-none outline-none font-medium py-2 placeholder:text-gray-300"
             />
          </div>

          <div className="space-y-6">
            {loading ? (
              <div className="py-20 text-center">
                <Loader2 size={40} className="animate-spin text-brand-indigo mx-auto mb-4" />
                <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest">Loading discussions...</p>
              </div>
            ) : filteredDiscussions.length > 0 ? (
              filteredDiscussions.map((disc) => (
                <DiscussionCard 
                  key={disc.id}
                  discussion={disc}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                  onVote={handleVote}
                />
              ))
            ) : (
              <EmptyState 
                icon={MessageCircle}
                title="No discussions found"
                description={search ? "We couldn't find any discussions matching your search." : "Be the first to start a conversation in this community hub."}
                action={!search && (
                  <button 
                    onClick={() => navigate(`/c/${id}/d/new`)}
                    className="flex items-center gap-2 bg-brand-indigo text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all"
                  >
                    <Plus size={18} />
                    Start a Topic
                  </button>
                )}
              />
            )}
          </div>
        </div>
      <ConfirmModal 
        {...confirmConfig}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
      />
    </div>
  );
};

export default CommunityDiscussions;
